import { memo, useMemo, useRef, useState, type RefObject } from "react";
import { useDebounceValue } from "usehooks-ts";
import { Loader2 } from "lucide-react";
import type { MarketingResource } from "creso-core/types";
import { Button, SearchInput, V2Loader } from "web-design-system";
import { Page } from "@core/components/layout/page";
import { usePaginatedMarketingResources } from "creso-core/hooks";
import { FilterList } from "@core/components/filter-list";
import {
  formatResourceDate,
  getResourceDate,
  getUpcomingWebinars,
  getRecentWebinar,
  isPastOrRecordedWebinar,
} from "creso-core/utils";
import { WebinarFeaturedCard } from "../components/webinar-featured-card";
import VideoCard from "../components/video-card";
import {
  getSelectedTopicIds,
  useResourceTopicFilters,
} from "../hooks/use-resource-topic-filters";
import { useInfiniteMarketingResources } from "../hooks/use-infinite-marketing-resources";

const PAGE_SIZE = 10;

const UpcomingWebinarSection = memo(function UpcomingWebinarSection({
  items,
}: {
  items: MarketingResource[];
}) {
  return (
    <section className="flex flex-col gap-5 pt-1">
      <div className="text-lg font-semibold">Upcoming Webinars</div>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {items.map((item) => (
          <WebinarFeaturedCard
            key={item.id}
            className="w-full xl:max-w-[620px]"
            item={item}
          />
        ))}
      </div>
    </section>
  );
});

const RecentWebinarSection = memo(function RecentWebinarSection({
  item,
}: {
  item: MarketingResource;
}) {
  return (
    <section className="flex flex-col gap-5 pt-1">
      <div className="text-lg font-semibold">Recent Webinar</div>
      <WebinarFeaturedCard
        className="max-w-[620px]"
        item={item}
        isUpcoming={false}
      />
    </section>
  );
});

function useFeaturedWebinars(
  topicIds: number[] | undefined,
  hideRecent: boolean
) {
  const { data: response } = usePaginatedMarketingResources({
    resource_type: "webinar",
    topic_ids: topicIds,
    page: 1,
    page_size: PAGE_SIZE,
  });

  const resources = useMemo(() => response?.results ?? [], [response]);

  const upcomingWebinars = useMemo(
    () => getUpcomingWebinars(resources),
    [resources]
  );

  const recentWebinar = useMemo(() => {
    if (hideRecent || upcomingWebinars.length > 0) return null;
    return getRecentWebinar(resources);
  }, [hideRecent, resources, upcomingWebinars.length]);

  return { upcomingWebinars, recentWebinar };
}

const FeaturedWebinarsPanel = memo(function FeaturedWebinarsPanel({
  upcomingWebinars,
  recentWebinar,
}: {
  upcomingWebinars: MarketingResource[];
  recentWebinar: MarketingResource | null;
}) {
  if (upcomingWebinars.length === 0 && !recentWebinar) {
    return null;
  }

  return (
    <div className="flex flex-col gap-9">
      {upcomingWebinars.length > 0 ? (
        <UpcomingWebinarSection items={upcomingWebinars} />
      ) : null}
      {recentWebinar ? <RecentWebinarSection item={recentWebinar} /> : null}
    </div>
  );
});

function PastWebinarsPanel({
  topicIds,
  topicIdsKey,
  excludedWebinarIds,
  hasActiveFilters,
  scrollContainerRef,
  onClearTopicFilters,
}: {
  topicIds: number[] | undefined;
  topicIdsKey: string;
  excludedWebinarIds: number[];
  hasActiveFilters: boolean;
  scrollContainerRef: RefObject<HTMLDivElement | null>;
  onClearTopicFilters: () => void;
}) {
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounceValue(search, 300);
  const excludedWebinarIdSet = useMemo(
    () => new Set(excludedWebinarIds),
    [excludedWebinarIds]
  );

  const {
    page,
    resourcesAccumulated,
    isFetching,
    isInitialLoading,
    loadMoreSentinelRef,
  } = useInfiniteMarketingResources({
    resource_type: "webinar",
    search: debouncedSearch || undefined,
    topic_ids: topicIds,
    page_size: PAGE_SIZE,
    listResetKey: `${debouncedSearch}|${topicIdsKey}`,
    scrollContainerRef,
  });

  const pastWebinars = useMemo(() => {
    return resourcesAccumulated.filter(
      (resource) =>
        !excludedWebinarIdSet.has(resource.id) &&
        isPastOrRecordedWebinar(resource)
    );
  }, [resourcesAccumulated, excludedWebinarIdSet]);

  const hasPastWebinarsSection = useMemo(() => {
    if (search.length > 0 || debouncedSearch.length > 0) {
      return true;
    }

    if (isInitialLoading) {
      return true;
    }

    return resourcesAccumulated.some(isPastOrRecordedWebinar);
  }, [search, debouncedSearch, isInitialLoading, resourcesAccumulated]);

  const showEmptyState =
    !isInitialLoading &&
    pastWebinars.length === 0 &&
    (debouncedSearch.length > 0 || hasActiveFilters);

  const clearFilters = () => {
    onClearTopicFilters();
    setSearch("");
  };

  if (!hasPastWebinarsSection && !showEmptyState) {
    return null;
  }

  return (
    <div className="flex flex-1 flex-col gap-9 h-full">
      {hasPastWebinarsSection ? (
        <section className="flex flex-col gap-5 pt-1 h-full">
          <div className="flex w-full flex-row items-center justify-between gap-1">
            <div className="text-lg font-semibold">Past Webinars</div>
            <div className="w-[320px] pr-1">
              <SearchInput
                placeholder="Search for topics"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>
          </div>
          <V2Loader isLoading={isInitialLoading}>
            {pastWebinars.length > 0 ? (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                {pastWebinars.map((item) => (
                  <VideoCard
                    key={item.id}
                    item={item}
                    content={
                      <div className="flex flex-col gap-2 p-3 pt-2">
                        <div className="line-clamp-2 text-sm font-medium">
                          {item.title}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {formatResourceDate(
                            getResourceDate(item) ?? "",
                            "dd MMM yyyy"
                          )}
                        </div>
                      </div>
                    }
                  />
                ))}
              </div>
            ) : null}
            <div
              ref={loadMoreSentinelRef}
              className="flex items-center justify-center"
            >
              {page > 1 && isFetching ? (
                <Loader2
                  className="size-5 animate-spin py-6 text-muted-foreground"
                  aria-hidden
                />
              ) : null}
            </div>
            {showEmptyState ? (
              <div className="flex h-full flex-col items-center justify-center rounded-lg border border-dashed border-border bg-card px-6 text-center">
                <div className="text-sm font-medium">
                  No webinars match the current search or selected filters.
                </div>
                <div className="mt-1 text-sm text-muted-foreground">
                  Try a different keyword or clear the applied filters.
                </div>
                <Button
                  variant="outline"
                  className="mt-4"
                  onClick={clearFilters}
                >
                  Clear filters
                </Button>
              </div>
            ) : null}
          </V2Loader>
        </section>
      ) : null}
    </div>
  );
}

export default function WebinarsPage() {
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const { filterOptions } = useResourceTopicFilters("webinar");
  const selectedTopicIds = getSelectedTopicIds(selectedFilters);
  const topicIdsKey = selectedTopicIds.join(",");
  const topicIds = selectedTopicIds.length > 0 ? selectedTopicIds : undefined;
  const hasActiveFilters = selectedFilters.length > 0;

  const { upcomingWebinars, recentWebinar } = useFeaturedWebinars(
    topicIds,
    hasActiveFilters
  );

  const excludedWebinarIds = useMemo(() => {
    const ids = upcomingWebinars.map((webinar) => webinar.id);

    if (!hasActiveFilters && recentWebinar) {
      ids.push(recentWebinar.id);
    }

    return ids;
  }, [hasActiveFilters, upcomingWebinars, recentWebinar]);

  const toggleOption = (option: string) => {
    setSelectedFilters((current) =>
      current.includes(option)
        ? current.filter((value) => value !== option)
        : [...current, option]
    );
  };

  const filters = useMemo(() => {
    return [
      {
        title: "Topics",
        options: filterOptions.map((option) => ({
          id: option.key,
          label: option.label,
          count: option.count,
          checked: selectedFilters.includes(option.key),
          onCheckedChange: () => toggleOption(option.key),
        })),
      },
    ];
  }, [filterOptions, selectedFilters]);

  return (
    <>
      <Page.Header
        title={
          <div className="flex flex-row items-center justify-between w-full">
            <h4 className="w-70">All Webinars</h4>
            <div className="lg:hidden">
              <FilterList
                showAccordion={false}
                filters={filters}
                onClearAll={() => setSelectedFilters([])}
              />
            </div>
          </div>
        }
      />
      <Page.Body className="overflow-hidden">
        <div className="flex flex-row overflow-hidden gap-4 flex-1">
          <div className="hidden self-stretch lg:block">
            <FilterList
              showAccordion={false}
              filters={filters}
              onClearAll={() => setSelectedFilters([])}
            />
          </div>

          <div className="flex flex-1 flex-col overflow-hidden">
            <div
              ref={scrollContainerRef}
              className="flex-1 overflow-y-auto pr-1"
            >
              <div className="flex flex-col gap-9 h-full">
                <FeaturedWebinarsPanel
                  upcomingWebinars={upcomingWebinars}
                  recentWebinar={recentWebinar}
                />
                <PastWebinarsPanel
                  topicIds={topicIds}
                  topicIdsKey={topicIdsKey}
                  excludedWebinarIds={excludedWebinarIds}
                  hasActiveFilters={hasActiveFilters}
                  scrollContainerRef={scrollContainerRef}
                  onClearTopicFilters={() => setSelectedFilters([])}
                />
              </div>
            </div>
          </div>
        </div>
      </Page.Body>
    </>
  );
}
